import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { useMachine } from "@xstate/react";
import { isMobile } from "react-device-detect";

import chatbotMachine from "../machines/chatbotMachine";
import Message from "./Message";
import Loader from "./Loader";
import Input from "./Input";
import profilePic from "../assets/support-icon.png";

const Chat = () => {
  const [state, send] = useMachine(chatbotMachine);
  const chatBottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isMobile) {
      inputRef.current && inputRef.current.focus();
    }
    chatBottomRef.current &&
      chatBottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [state, chatBottomRef]);

  const blurInput = () => {
    inputRef.current && inputRef.current.blur();
  };

  return (
    <Position>
      <Container>
        <InfoMessage>
          <img
            src="https://benepath.net/bot.webp"
            alt="Sales representative"
          />
          Hey, how can I help you today?
        </InfoMessage>

        {state.context.messages.map((message, i) => (
          <MessageWrapper key={i}>
            <Message sender={message.sender} message={message.message} />
          </MessageWrapper>
        ))}

        {state.matches("loading") && <Loader />}

        {/* Div is used as an anchor for auto-scrolling */}
        <div style={{ float: "left", clear: "both" }} ref={chatBottomRef}></div>
      </Container>

      <InputWrapper>
        <Input
          placeholder="Type in your message..."
          onChange={(e) => send("CHANGE", { message: e.target.value })}
          onKeyPress={(e) => {
            if (e.key === "Enter") {
              send("SEND");
              blurInput();
            }
          }}
          value={state.context.message}
          disabled={state.matches("loading")}
          ref={inputRef}
        />

        <SendButton
          onClick={() => {
            send("SEND");
            blurInput();
          }}
          disabled={state.matches("loading")}
        >
          Send
        </SendButton>
      </InputWrapper>
    </Position>
  );
};
const Position = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #ebf2f9;
`;

const Container = styled.div`
  padding: 24px 24px 96px;
  margin-bottom: -96px;
  flex-grow: 1;
  height: 80vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const MessageWrapper = styled.div`
  margin: 0 0 16px 0;
`;

const InputWrapper = styled.div`
  margin: 16px;
  height: 64px;
  display: inline-flex;
  border-radius: 16px;
  position: relative;
  z-index: 1;
  box-shadow: 16px 4px 64px rgba(18, 61, 101, 0.3),
    0px 0px 40px rgba(255, 255, 255, 0.8);
  input {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    padding-right: 80px;
    :focus {
      border-right: none;
      padding-right: 96px;
    }
  }
`;

const InfoMessage = styled.div`
  border-radius: 16px;
  background: #ffffff;
  padding: 16px;
  font-size: 16px;
  letter-spacing: 0.02em;
  line-height: 140%;
  margin-bottom: 32px;
  display: flex;
  align-items: center;
  box-shadow: 0px 0px 20px rgba(255, 255, 255, 0.8),
    32px -8px 128px rgba(18, 46, 101, 0.14);
  img {
    width: 64px;
    height: 64px;
    margin-right: 16px;
    border-radius: 100%;
    object-fit: contain;
  }
`;

const SendButton = styled.button`
  border: none;
  border-radius: 0 16px 16px 0;
  background-color: #735cdd;
  color: #ffffff;
  width: 80px;
  height: 100%;
  cursor: pointer;
  outline: none;
  position: absolute;
  right: 0;
  top: 0;
  font-size: 16px;
  text-align: center;
  transition: all 0.1s ease-in-out;
  &::-moz-focus-inner {
    border: 0;
  }
  &:hover {
    background-color: #a596e9;
  }
  :disabled {
    background-color: #a596e9;
  }
`;

export default Chat;
